import React, { FC, useState, useCallback } from 'react';

import Table from 'molecules/Table';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import Link from 'atoms/Link/Link';
import GoogleSalesReportService from './Service/GoogleSalesReportService';

import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import { StyledInvoiceStatus } from 'organisms/SalesReportTable/SalesReportTable.styles';

import dayjs from 'dayjs';
import { snakeCase } from 'lodash-es';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';

import { Order } from 'organisms/InvoicesTable/types';

export interface ISalesReportService {
  amount: number;
  description: string;
  name: string;
  value: string;
}

export interface GoogleSalesReportRow {
  company_name: string;
  sale_rep_name: string;
  account_manager_name: string;
  close_date: string;
  invoice_date: string;
  invoice_number: string;
  invoice_amount: number;
  invoice_status: 'paid' | 'unpaid';
  invoice_qb_id: string;
  id: string;
  currency: string;
  currency_symbol: string;
  company_hs_id: string;
  sale_rep_email: string;
  account_manager_email: string;
  due_date: null | string;
  paid_date: null | string;
  service_description: string;
  service_reference: string;
  services: ISalesReportService[];
  total: number;
  total_tax_amount: number;
  discount_amount: number;
  subtotal: number;
  amount_due: number;
  paid_total: number;
  transaction_fee: number;
  link: string;
  payments_credits: number;
  google_sales_rep_email: string;
  google_sales_rep_name: string;
  company_google_sales_rep_email: string;
  company_googler_email: string;
  company_googler_name: string;
  deal_stage?: string;
  deal_amount?: number;
  deal_company_googler_name: string;
  deal_googler_name: string;
  deal_company_googler_email: string;
  deal_googler_email: string;
}

export type salesRepSortBy =
  | 'google_sales_rep_name'
  | 'company_name'
  | 'invoice_date'
  | 'close_date'
  | 'invoice_amount';

export const initialValue = { page: 1, limit: 20 };

const GoogleSalesReportTable: FC<{
  isForecast?: boolean;
  loading?: boolean;
  data: { invoices: GoogleSalesReportRow[]; count: number };
  onChange: (f: typeof initialValue, s?: string) => void;
}> = ({
  loading,
  isForecast,
  data: { invoices = [], count = 0 },
  onChange,
}) => {
  const [activeRow, setActiveRow] = useState<number>();

  const [pagination, setPagination] = useState(initialValue);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<salesRepSortBy>(
    'google_sales_rep_name'
  );

  const handleRequestSort = useCallback(
    async (event: React.MouseEvent<unknown>, property: salesRepSortBy) => {
      const isAsc = orderBy === property && order === 'asc';
      const nextState = { ...pagination, page: 1 };

      onChange(
        nextState,
        `sort[${snakeCase(property)}]=${isAsc ? 'desc' : 'asc'}`
      );

      setPagination(nextState);
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangePage = useCallback(
    async (event: unknown, newPage: number) => {
      const nextState = { ...pagination, page: newPage + 1 };
      const sort =
        order && orderBy ? `sort[${snakeCase(orderBy)}]=${order}` : '';

      onChange(nextState, sort);
      setPagination(nextState);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangeRowsPerPage = useCallback(
    async (option: number) => {
      const nextState = { limit: option, page: 1 };
      const sort =
        order && orderBy ? `sort[${snakeCase(orderBy)}]=${order}` : '';

      onChange(nextState, sort);
      setPagination(nextState);
    },
    [orderBy, order, pagination, onChange]
  );

  const handleRowClick = useCallback(
    (event, row) => {
      if (window.getSelection()?.type === 'Range') return;

      setActiveRow(activeRow === row.id ? 0 : row.id);
    },
    [activeRow]
  );

  const rowDetailsRenderer = useCallback(
    (row: GoogleSalesReportRow) => <GoogleSalesReportService row={row} />,
    []
  );

  function setRowKey(row: GoogleSalesReportRow, index: number) {
    return { ...row, id: `${row.invoice_date}-${index}` };
  }

  const columns = [
    {
      key: 'google_sales_rep_email',
      label: 'Name',
      greyText: 'Email',
      sortable: true,
      width: '25%',
      render: ({
        deal_company_googler_name,
        deal_googler_name,
        deal_company_googler_email,
        deal_googler_email,
      }: GoogleSalesReportRow) => (
        // if (!company_google_sales_rep_email && !google_sales_rep_email)
        //   return null;
        // if (company_google_sales_rep_email === google_sales_rep_email) {
        //   return (
        //     <StyledCellContainer>
        //       <StyledCellText>{company_google_sales_rep_email}</StyledCellText>
        //     </StyledCellContainer>
        //   );
        // }
        <StyledCellContainer>
          <StyledCellText>
            {deal_company_googler_name?.trim() || deal_googler_name?.trim()}
          </StyledCellText>
          <StyledCellText $textType="grey">
            {deal_company_googler_email?.trim() || deal_googler_email?.trim()}
          </StyledCellText>
        </StyledCellContainer>
      ),
      // },
    },
    {
      key: 'invoice_date',
      label: 'Invoice Date',
      greyText: 'Invoice #',
      sortable: true,
      width: '12%',
      render: ({ invoice_number, invoice_date }: GoogleSalesReportRow) => (
        <StyledCellContainer $vStack>
          <StyledCellText>
            {invoice_date
              ? dayjs(dayjs(invoice_date)).format('MMM DD, YYYY')
              : ''}
          </StyledCellText>
          <StyledCellText $textType="grey">
            {invoice_number ? `#${invoice_number}` : ''}
          </StyledCellText>
        </StyledCellContainer>
      ),
    },
    {
      key: 'company_name',
      label: 'Company name',
      sortable: true,
      width: '20%',
      render: ({ company_name, company_hs_id }: GoogleSalesReportRow) => (
        <Link to={`/company/${company_hs_id}`} $textDecoration="none">
          <StyledCellText $textType="link">{company_name}</StyledCellText>
        </Link>
      ),
    },
    {
      key: 'invoice_amount',
      label: 'Weighted Amount',
      sortable: true,
      width: '10%',
      render: ({ subtotal, currency: rowCurrency }: GoogleSalesReportRow) => (
        <StyledCellContainer $hStack $right>
          <StyledCellText>
            {subtotal ? currencyFormatter(subtotal, rowCurrency) : ''}
          </StyledCellText>
        </StyledCellContainer>
      ),
    },
    ...(isForecast
      ? [
          {
            key: 'deal_amount',
            label: 'Deal Amount',
            sortable: true,
            width: '10%',
            render: ({
              deal_amount,
              invoice_status,
              currency,
            }: GoogleSalesReportRow) => (
              <StyledCellContainer $hStack $right>
                <StyledCellText>
                  {deal_amount &&
                  invoice_status !== 'paid' &&
                  invoice_status !== 'unpaid'
                    ? currencyFormatter(deal_amount, currency)
                    : ''}
                </StyledCellText>
              </StyledCellContainer>
            ),
          },
        ]
      : []),
    {
      key: 'deal_stage',
      label: 'Deal Stage',
      sortable: true,
      width: '15%',
      render: ({ deal_stage }: GoogleSalesReportRow) => (
        <StyledCellContainer $hStack $right>
          <StyledCellText>{deal_stage || ''}</StyledCellText>
        </StyledCellContainer>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: '15%',
      render: ({ invoice_status }: GoogleSalesReportRow) => (
        <StyledCellContainer $hStack $left>
          <StyledInvoiceStatus $status={invoice_status}>
            {invoice_status}
          </StyledInvoiceStatus>
        </StyledCellContainer>
      ),
    },
  ];

  return (
    <StyledTableContainer>
      <Table
        data={invoices?.map(setRowKey) || []}
        columns={columns}
        idKey="id"
        sx={{ minWidth: 650 }}
        aria-label="simple table"
        loading={loading}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
        collapsible
        collapsedRow={activeRow}
        onRowClick={handleRowClick}
        rowDetailsRenderer={rowDetailsRenderer}
      />
      <StyledTablePagination
        labelDisplayedRows={() => null}
        rowsPerPageOptions={[]}
        count={count}
        rowsPerPage={pagination.limit}
        page={count ? pagination.page - 1 : 0}
        onPageChange={handleChangePage}
        ActionsComponent={(props) => (
          <PaginationActions
            {...props}
            labelRowsPerPage="Items per page"
            rowsPerPageOptions={[20, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      />
    </StyledTableContainer>
  );
};

export default GoogleSalesReportTable;
